import React from "react";

import {
  ME_INSIGHTS_BASE,
  downloadMyBreakdownCsv,
  getMyBudgetDrivers,
  listMyBudgets,
} from "../../lib/api";
import type { AuthHeaders } from "../../lib/auth";
import type { Breakdown, Me, SpendBudget } from "../../lib/types";
import { Sessions } from "../Sessions";
import { ActivityPanel } from "./ActivityPanel";
import { EntityHost } from "./Entity";
import { PersonalSummary } from "../Overview";
import { Savings } from "./Savings";
import { Assets } from "./Assets";
import {
  AskForMore,
  HealthHead,
  MemberHealth,
  RaiseAction,
  SpendCell,
  SpendHead,
} from "./BudgetBar";
import { TimePresets, usePreset } from "./range";
import {
  Callout,
  Button,
  Card,
  Grid,
  Page,
  SectionHead,
  Skeleton,
  Table,
  type Layer,
} from "../../design";
import { FilterBar, SpendExplorer } from "./SpendExplorer";
import { UseCasesView } from "./UseCases";
import { CostDrivers, Insights, type Ctx } from "./Visibility";
import { BarList } from "./shared";
import { filtersOf, shortRepo, usdShort, type ActiveFilter } from "./util";
import type { InsightDimension } from "../../lib/types";
import { fmtDate } from "../../lib/format";

type Tab =
  | "overview"
  | "spend"
  | "drivers"
  | "sessions"
  | "usecases"
  | "savings";

export const USAGE_LAYERS: Layer<Tab>[] = [
  { value: "overview", label: "Summary", icon: "dashboard", group: "Analysis" },
  { value: "spend", label: "Spend", icon: "spend", group: "Analysis" },
  {
    value: "drivers",
    label: "Cost drivers",
    icon: "trend-up",
    group: "Analysis",
  },
  { value: "usecases", label: "Use cases", icon: "target", group: "Analysis" },
  { value: "sessions", label: "Sessions", icon: "clock", group: "Records" },
  { value: "savings", label: "Savings", icon: "trend-down", group: "Records" },
];

function tabOf(sub: string | undefined): Tab | undefined {
  if (sub === "tools") return "spend";
  return USAGE_LAYERS.find((t) => t.value === sub)?.value;
}

/** One personal page: your spend, sessions and tools across every
 * organization you work in, plus condense's own metering under Savings. */
export function MyUsage({
  authHeaders,
  me,
  refresh,
  sub,
  onSub,
}: {
  authHeaders: AuthHeaders;
  me: Me;
  refresh: () => Promise<void>;
  sub?: string;
  onSub?: (tab: string) => void;
}) {
  const [tabState, setTabState] = React.useState<Tab>("overview");
  const tab = tabOf(sub) ?? (onSub ? "overview" : tabState);
  const setTab = (t: Tab) => {
    setTabState(t);
    onSub?.(t);
  };
  const [preset, setPreset, range] = usePreset();
  const [filters, setFilters] = React.useState<ActiveFilter[]>([]);
  const [budgets, setBudgets] = React.useState<SpendBudget[]>([]);
  const [budgetError, setBudgetError] = React.useState(false);
  const [budgetRevision, reloadBudgets] = React.useReducer(
    (n: number) => n + 1,
    0,
  );
  React.useEffect(() => {
    let active = true;
    let pending = false;
    const load = async () => {
      if (pending || document.hidden) return;
      pending = true;
      try {
        const next = await listMyBudgets(authHeaders);
        if (active) {
          setBudgets(next);
          setBudgetError(false);
        }
      } catch {
        if (active) setBudgetError(true);
      } finally {
        pending = false;
      }
    };
    void load();
    const timer = window.setInterval(() => void load(), 15000);
    window.addEventListener("focus", load);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener("focus", load);
    };
  }, [authHeaders, budgetRevision]);
  const drill = (f: ActiveFilter) =>
    setFilters((cur) => [...cur.filter((x) => x.key !== f.key), f]);
  const csv = (dim: InsightDimension) =>
    downloadMyBreakdownCsv(
      authHeaders,
      range,
      dim === "none" ? "repo" : dim,
      filtersOf(filters),
    );
  const ctx: Ctx = {
    authHeaders,
    base: ME_INSIGHTS_BASE,
    range,
    filters,
    onDrill: drill,
    csv,
  };

  return (
    <EntityHost
      authHeaders={authHeaders}
      base={ME_INSIGHTS_BASE}
      onFilter={(f) => {
        drill(f);
        setTab("spend");
      }}
    >
      <Page
        title="My usage"
        layers={{ value: tab, options: USAGE_LAYERS, onChange: setTab }}
        actions={<TimePresets value={preset} onChange={setPreset} />}
      >
        <FilterBar
          filters={filters}
          onRemove={(k) => setFilters((cur) => cur.filter((f) => f.key !== k))}
        />
        {tab === "overview" ? (
          <PersonalSummary
            me={me}
            authHeaders={authHeaders}
            refresh={refresh}
            range={range}
            onTab={setTab}
            limits={
              <>
                {budgetError && (
                  <Callout
                    tone="warn"
                    action={
                      <Button size="sm" onClick={reloadBudgets}>
                        Retry
                      </Button>
                    }
                  >
                    Budget status could not be updated.
                  </Callout>
                )}
                {budgets.length > 0 && (
                  <MyBudgets
                    budgets={budgets}
                    authHeaders={authHeaders}
                    onRefresh={reloadBudgets}
                  />
                )}
              </>
            }
          />
        ) : tab === "spend" ? (
          <>
            <SpendExplorer
              authHeaders={authHeaders}
              base={ME_INSIGHTS_BASE}
              range={range}
              dimensions={["repo", "model", "kind", "client", "cost_basis"]}
              filters={filters}
              onDrill={drill}
              peopleLabel={null}
              bare
              onCsv={csv}
            />
            <Assets
              authHeaders={authHeaders}
              base={ME_INSIGHTS_BASE}
              range={range}
              showUsers={false}
            />
          </>
        ) : tab === "drivers" ? (
          <>
            <CostDrivers
              ctx={ctx}
              by={[
                ["repo", "By repository"],
                ["model", "By model"],
              ]}
            />
            <SectionHead
              title="What changed"
              info="This range against the range of the same length just before it."
            />
            <Insights ctx={ctx} org={false} />
          </>
        ) : tab === "sessions" ? (
          <>
            <Sessions
              authHeaders={authHeaders}
              zdr={!!me.toggles?.zdr || !!me.toggles?.zdr_enforced}
            />
            <ActivityPanel
              authHeaders={authHeaders}
              base={ME_INSIGHTS_BASE}
              range={range}
              filters={filters}
              showPeople={false}
              bare
            />
          </>
        ) : tab === "usecases" ? (
          <UseCasesView
            authHeaders={authHeaders}
            base={ME_INSIGHTS_BASE}
            range={range}
            filters={filtersOf(filters)}
            org={false}
          />
        ) : (
          <Savings authHeaders={authHeaders} me={me} range={range} />
        )}
      </Page>
    </EntityHost>
  );
}

function MyBudgets({
  budgets,
  authHeaders,
  onRefresh,
}: {
  budgets: SpendBudget[];
  authHeaders: AuthHeaders;
  onRefresh: () => void;
}) {
  const [open, setOpen] = React.useState<string | null>(null);
  const [asking, setAsking] = React.useState<SpendBudget | null>(null);
  const shown = budgets.filter((b) => b.scope !== "org");
  const orgStop = budgets.find(
    (b) => b.scope === "org" && b.state === "exceeded" && b.action !== "warn",
  );
  const raise = (b: SpendBudget) => (
    <RaiseAction b={b} asked={!!b.raise_pending} onAsk={() => setAsking(b)} />
  );
  return (
    <>
      {orgStop && (
        <Callout
          tone="bad"
          title="Your organization is over its limit"
          action={raise(orgStop)}
        >
          {orgStop.action === "block"
            ? "Requests are paused until an admin raises the limit."
            : "Each new request needs an admin's approval until the limit resets."}
        </Callout>
      )}
      {shown.length > 0 && (
        <Card
          flush
          title="Current budgets"
          info="Limits your admins set on provider spend for you or your team. A team budget counts everyone on the team, not only you."
        >
          <Table label="Your budgets">
            <thead>
              <tr>
                <th>Budget</th>
                <th>
                  <SpendHead />
                </th>
                <th>
                  <HealthHead />
                </th>
                <th className="actions" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {shown.map((b) => {
                const drivers = b.scope === "team" || b.scope === "user";
                const expanded = open === b.id;
                return (
                  <React.Fragment key={b.id}>
                    <tr>
                      <td>
                        <strong>{b.name}</strong>
                        <span className="ds-cell-sub">
                          {b.scope === "team"
                            ? "Team"
                            : b.scope === "user"
                              ? "Personal"
                              : b.scope === "model"
                                ? "Model"
                                : "API key"}{" "}
                          · {b.period === "month" ? "Monthly" : "Weekly"} ·
                          Through {fmtDate(b.period_end)}
                        </span>
                      </td>
                      <td className="gov-spendtd">
                        <SpendCell b={b} />
                      </td>
                      <td>
                        <MemberHealth b={b} />
                      </td>
                      <td className="actions per-budget-acts">
                        {raise(b)}
                        {drivers && (
                          <Button
                            variant="link"
                            size="sm"
                            iconRight={
                              expanded ? "chevron-down" : "chevron-right"
                            }
                            aria-expanded={expanded}
                            aria-controls={`drivers-${b.id}`}
                            onClick={() => setOpen(expanded ? null : b.id)}
                          >
                            What drove it
                          </Button>
                        )}
                      </td>
                    </tr>
                    {expanded && (
                      <tr
                        className="per-budget-drivers-row"
                        id={`drivers-${b.id}`}
                      >
                        <td colSpan={4}>
                          <BudgetDrivers budget={b} authHeaders={authHeaders} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </Card>
      )}
      {asking && (
        <AskForMore
          b={asking}
          authHeaders={authHeaders}
          onClose={() => setAsking(null)}
          onSent={() => {
            onRefresh();
            setAsking(null);
          }}
        />
      )}
    </>
  );
}

const TOP_DRIVERS = 5;

/** A team or personal budget's own spend this period, by model and by
 * repo. Each list adds up to the budget's spend: the rollup behind the lists
 * trails today's spend, so "Everything else" carries the gap too. */
function BudgetDrivers({
  budget,
  authHeaders,
}: {
  budget: SpendBudget;
  authHeaders: AuthHeaders;
}) {
  const [data, setData] = React.useState<
    { model: Breakdown; repo: Breakdown } | null | undefined
  >(undefined);
  React.useEffect(() => {
    Promise.all([
      getMyBudgetDrivers(authHeaders, budget.id, "model"),
      getMyBudgetDrivers(authHeaders, budget.id, "repo"),
    ]).then(
      ([model, repo]) => setData({ model, repo }),
      () => setData(null),
    );
  }, [authHeaders, budget.id]);
  const cost = (m: Breakdown["totals"]) =>
    Number(budget.basis === "metered" ? m.cost_metered_usd : m.cost_usd);
  const rows = (b: Breakdown) => {
    const all = b.groups
      .map((g) => ({
        key: g.key ?? "none",
        label: g.key == null ? "No repo" : shortRepo(g.label),
        value: cost(g.metrics),
      }))
      .filter((r) => r.value > 0)
      .sort((x, y) => y.value - x.value);
    const top = all.slice(0, TOP_DRIVERS);
    const total = Math.max(cost(b.totals), Number(budget.spent_usd));
    const rest = total - top.reduce((a, r) => a + r.value, 0);
    return rest >= 0.005
      ? [...top, { key: "__other", label: "Everything else", value: rest }]
      : top;
  };
  if (data === undefined) return <Skeleton lines={3} />;
  if (data === null)
    return <p className="per-muted">Couldn&apos;t load what drove it.</p>;
  const byModel = rows(data.model);
  const byRepo = rows(data.repo);
  const who =
    budget.scope === "team"
      ? " by everyone on the team, you included"
      : " by you";
  return (
    <div className="per-budget-drivers">
      <p className="per-budget-drivers-head">
        <strong>{usdShort(budget.spent_usd)}</strong> spent so far this{" "}
        {budget.period}
        {who}.{byModel.length > 0 && " Where it went:"}
      </p>
      {byModel.length === 0 && byRepo.length === 0 ? (
        <p className="per-muted">
          No spend on this budget yet this {budget.period}.
        </p>
      ) : (
        <Grid cols={2}>
          <div>
            <div className="per-row-title">By model</div>
            <BarList rows={byModel} format={(v) => usdShort(v)} />
          </div>
          <div>
            <div className="per-row-title">By repository</div>
            <BarList rows={byRepo} format={(v) => usdShort(v)} />
          </div>
        </Grid>
      )}
    </div>
  );
}
